import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Edit3, Loader2, Plus, Save, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
    CATEGORIES,
    INDUSTRIES,
    findCategory,
    findIndustry,
    slugify,
} from "../data/libraryMeta";

const EMPTY = {
    id: null,
    title: "",
    slug: "",
    excerpt: "",
    body: "",
    category: CATEGORIES[0].slug,
    industries: [],
    cover_image_url: "",
    is_published: true,
};

export default function AdminLibrary() {
    const { user, loading } = useAuth();
    const nav = useNavigate();
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [loadingList, setLoadingList] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (loading) return;
        if (!user) {
            nav("/admin/login");
            return;
        }
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, loading]);

    async function reload() {
        setLoadingList(true);
        try {
            const { data } = await api.get("/admin/library");
            setItems(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load");
        } finally {
            setLoadingList(false);
        }
    }

    async function save(e) {
        e?.preventDefault?.();
        if (!editing.title.trim() || !editing.excerpt.trim() || !editing.body.trim()) {
            toast.error("Title, excerpt and body are required.");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                title: editing.title,
                slug: editing.slug || slugify(editing.title),
                excerpt: editing.excerpt,
                body: editing.body,
                category: editing.category,
                industries: editing.industries,
                cover_image_url: editing.cover_image_url || null,
                is_published: editing.is_published,
            };
            if (editing.id) {
                await api.patch(`/admin/library/${editing.id}`, payload);
                toast.success("Resource updated");
            } else {
                await api.post("/admin/library", payload);
                toast.success("Resource published");
            }
            setEditing(null);
            reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save");
        } finally {
            setSaving(false);
        }
    }

    async function del(id) {
        if (!window.confirm("Delete this resource? This can't be undone.")) return;
        try {
            await api.delete(`/admin/library/${id}`);
            toast.success("Deleted");
            reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't delete");
        }
    }

    function toggleIndustry(slug) {
        setEditing((e) => {
            const has = e.industries.includes(slug);
            return {
                ...e,
                industries: has
                    ? e.industries.filter((x) => x !== slug)
                    : [...e.industries, slug],
            };
        });
    }

    return (
        <div className="min-h-screen bg-page text-ink" data-testid="admin-library-page">
            <header className="border-b-2 border-ink/10 bg-white">
                <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-5 lg:px-10">
                    <div className="flex items-center gap-3">
                        <Link
                            to="/admin"
                            className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-ink/60 hover:text-coral"
                            data-testid="admin-library-back"
                        >
                            <ArrowLeft className="h-4 w-4" /> Dashboard
                        </Link>
                        <span className="text-ink/25">/</span>
                        <span className="font-display text-lg font-black uppercase tracking-tight text-ink">
                            Library
                        </span>
                    </div>
                    {!editing && (
                        <button
                            onClick={() => setEditing({ ...EMPTY })}
                            className="inline-flex items-center gap-2 border-2 border-coral bg-coral px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white hover:-translate-y-0.5 transition-all"
                            data-testid="admin-library-new"
                        >
                            <Plus className="h-4 w-4" />
                            New resource
                        </button>
                    )}
                </div>
            </header>

            <main className="mx-auto max-w-[1400px] px-5 py-10 lg:px-10">
                {editing ? (
                    <form
                        onSubmit={save}
                        className="max-w-[900px] border-2 border-ink/15 bg-white p-8 lg:p-10"
                        data-testid="admin-library-form"
                    >
                        <div className="mb-8 flex items-center justify-between">
                            <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-coral">
                                {editing.id ? "Edit resource" : "New resource"}
                            </div>
                            <button
                                type="button"
                                onClick={() => setEditing(null)}
                                className="text-ink/45 hover:text-coral"
                                aria-label="Close"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <Field label="Title">
                            <input
                                value={editing.title}
                                onChange={(e) =>
                                    setEditing({
                                        ...editing,
                                        title: e.target.value,
                                        slug: editing.id ? editing.slug : slugify(e.target.value),
                                    })
                                }
                                className="w-full border-2 border-ink/20 bg-white px-4 py-3 text-ink focus:border-coral focus:outline-none"
                                data-testid="admin-library-title"
                                required
                            />
                        </Field>

                        <Field label="URL slug (auto-generated — you can edit)">
                            <input
                                value={editing.slug}
                                onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                                className="w-full border-2 border-ink/20 bg-white px-4 py-3 font-mono text-sm text-ink focus:border-coral focus:outline-none"
                                data-testid="admin-library-slug"
                            />
                        </Field>

                        <Field label="Excerpt (1–2 sentences shown on the card)">
                            <textarea
                                value={editing.excerpt}
                                onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
                                rows={2}
                                className="w-full border-2 border-ink/20 bg-white px-4 py-3 text-ink focus:border-coral focus:outline-none"
                                data-testid="admin-library-excerpt"
                                required
                            />
                        </Field>

                        <Field label="Body (double line break = new paragraph)">
                            <textarea
                                value={editing.body}
                                onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                                rows={14}
                                className="w-full border-2 border-ink/20 bg-white px-4 py-3 font-mono text-sm text-ink focus:border-coral focus:outline-none"
                                data-testid="admin-library-body"
                                required
                            />
                        </Field>

                        <div className="grid gap-6 lg:grid-cols-2">
                            <Field label="Category">
                                <select
                                    value={editing.category}
                                    onChange={(e) =>
                                        setEditing({ ...editing, category: e.target.value })
                                    }
                                    className="w-full border-2 border-ink/20 bg-white px-4 py-3 text-ink focus:border-coral focus:outline-none"
                                    data-testid="admin-library-category"
                                >
                                    {CATEGORIES.map((c) => (
                                        <option key={c.slug} value={c.slug}>
                                            {c.label}
                                        </option>
                                    ))}
                                </select>
                            </Field>

                            <Field label="Cover image URL (optional)">
                                <input
                                    value={editing.cover_image_url || ""}
                                    onChange={(e) =>
                                        setEditing({
                                            ...editing,
                                            cover_image_url: e.target.value,
                                        })
                                    }
                                    className="w-full border-2 border-ink/20 bg-white px-4 py-3 text-ink focus:border-coral focus:outline-none"
                                    data-testid="admin-library-cover"
                                    placeholder="https://…"
                                />
                            </Field>
                        </div>

                        <Field label="Industries (pick as many as apply)">
                            <div className="flex flex-wrap gap-2">
                                {INDUSTRIES.map((i) => {
                                    const active = editing.industries.includes(i.slug);
                                    return (
                                        <button
                                            key={i.slug}
                                            type="button"
                                            onClick={() => toggleIndustry(i.slug)}
                                            className={`border-2 px-3 py-1.5 font-display text-xs font-bold uppercase tracking-wider transition-all ${
                                                active
                                                    ? "border-coral bg-coral text-white"
                                                    : "border-ink/15 bg-white text-ink hover:border-coral"
                                            }`}
                                            data-testid={`admin-library-industry-${i.slug}`}
                                        >
                                            <span className="mr-1.5">{i.emoji}</span>
                                            {i.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </Field>

                        <label className="mt-2 flex items-center gap-3 text-sm text-ink/80">
                            <input
                                type="checkbox"
                                checked={editing.is_published}
                                onChange={(e) =>
                                    setEditing({ ...editing, is_published: e.target.checked })
                                }
                                className="h-4 w-4"
                                data-testid="admin-library-published"
                            />
                            <span>Published (visible to the public)</span>
                        </label>

                        <div className="mt-10 flex items-center gap-4">
                            <button
                                type="submit"
                                disabled={saving}
                                className="inline-flex items-center gap-2 border-2 border-coral bg-coral px-6 py-3 font-display text-sm font-bold uppercase tracking-wider text-white transition-all hover:-translate-y-0.5 disabled:opacity-60"
                                data-testid="admin-library-save"
                            >
                                {saving ? (
                                    <>
                                        Saving…
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    </>
                                ) : (
                                    <>
                                        <Save className="h-4 w-4" />
                                        {editing.id ? "Update" : "Publish"}
                                    </>
                                )}
                            </button>
                            {editing.id && (
                                <button
                                    type="button"
                                    onClick={() => del(editing.id)}
                                    className="inline-flex items-center gap-2 border-2 border-ink/25 bg-white px-6 py-3 font-display text-sm font-bold uppercase tracking-wider text-ink hover:border-coral hover:text-coral"
                                    data-testid="admin-library-delete"
                                >
                                    <Trash2 className="h-4 w-4" />
                                    Delete
                                </button>
                            )}
                        </div>
                    </form>
                ) : (
                    <div className="max-w-[1100px]">
                        {loadingList ? (
                            <div className="text-sm uppercase tracking-widest text-ink/45">
                                Loading…
                            </div>
                        ) : items.length === 0 ? (
                            <div className="border-2 border-dashed border-ink/20 bg-white p-14 text-center">
                                <div className="font-display text-2xl font-black text-ink">
                                    No resources yet.
                                </div>
                                <p className="mt-3 text-sm text-ink/70">
                                    Add your first fortnightly resource — takes about 5 minutes.
                                </p>
                                <button
                                    onClick={() => setEditing({ ...EMPTY })}
                                    className="mt-6 inline-flex items-center gap-2 border-2 border-coral bg-coral px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white"
                                    data-testid="admin-library-first"
                                >
                                    <Plus className="h-4 w-4" />
                                    Create resource
                                </button>
                            </div>
                        ) : (
                            <div className="divide-y divide-ink/10 border-2 border-ink/15 bg-white">
                                {items.map((r) => (
                                    <div
                                        key={r.id}
                                        className="flex items-center justify-between p-5"
                                        data-testid={`admin-library-row-${r.slug}`}
                                    >
                                        <div className="min-w-0">
                                            <div className="flex flex-wrap items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-coral">
                                                <span>{findCategory(r.category)?.label}</span>
                                                {!r.is_published && (
                                                    <span className="border border-ink/25 px-2 py-0.5 text-ink/60">
                                                        Draft
                                                    </span>
                                                )}
                                            </div>
                                            <div className="mt-1 truncate font-display text-lg font-black text-ink">
                                                {r.title}
                                            </div>
                                            <div className="mt-1 flex flex-wrap gap-2 text-[10px] uppercase tracking-widest text-ink/50">
                                                {r.industries.map((ind) => {
                                                    const meta = findIndustry(ind);
                                                    return meta ? (
                                                        <span key={ind}>
                                                            {meta.emoji} {meta.label}
                                                        </span>
                                                    ) : null;
                                                })}
                                            </div>
                                        </div>
                                        <div className="flex shrink-0 gap-2">
                                            <button
                                                onClick={() =>
                                                    setEditing({
                                                        ...r,
                                                        cover_image_url: r.cover_image_url || "",
                                                    })
                                                }
                                                className="inline-flex items-center gap-2 border-2 border-ink/25 bg-white px-4 py-2 font-display text-xs font-bold uppercase tracking-wider text-ink hover:border-coral hover:text-coral"
                                                data-testid={`admin-library-edit-${r.slug}`}
                                            >
                                                <Edit3 className="h-3.5 w-3.5" /> Edit
                                            </button>
                                            <button
                                                onClick={() => del(r.id)}
                                                className="inline-flex items-center gap-2 border-2 border-ink/25 bg-white px-4 py-2 font-display text-xs font-bold uppercase tracking-wider text-ink hover:border-coral hover:text-coral"
                                                data-testid={`admin-library-del-${r.slug}`}
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </main>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="mb-6 block">
            <span className="mb-2 block text-[11px] font-bold uppercase tracking-[0.22em] text-ink/60">
                {label}
            </span>
            {children}
        </label>
    );
}
